import { Box, Flex, Text } from '@chakra-ui/react';
import React, { useState } from 'react';

import { CreditAgreement } from '@finley-technologies/finley-types';

import { ReportDataCellConfigView } from './ReportDataCellConfigView';
import { ReportSpreadsheetConfigView } from './ReportSpreadsheetConfigView';
import {
  Select,
  formatSelectOptions,
} from 'packages/common-frontend/components/Select';
import { CreditAgreementFullArtifactWipConfigType } from 'packages/creditAgreements/ui/types';

type DataCellObjectConfig = CreditAgreement.Config.Reporting.Outputs.Xlsx.DataCellObjectConfig;

type ViewDataCellObjectConfigProps = {
  sheetIdxConfigPropertyKey: string;
  targetSheetConfig: DataCellObjectConfig;
};
const ViewDataCellObjectConfig = ({
  sheetIdxConfigPropertyKey,
  targetSheetConfig,
}: ViewDataCellObjectConfigProps) => {
  return (
    <ReportDataCellConfigView
      sheetIdxConfigPropertyKey={sheetIdxConfigPropertyKey}
      targetSheetConfig={targetSheetConfig}
      // Since "view" mode, fine to pass in empty results as this is not used
      queryResults={[]}
    />
  );
};

const EditModeIterable = ['Spreadsheet', 'Data Cell'] as const;
type EditMode = typeof EditModeIterable[number];

type EditDataCellObjectConfigProps = {
  wipConfig: CreditAgreementFullArtifactWipConfigType;
  sheetIdxConfigPropertyKey: string;
  targetSheetConfig: DataCellObjectConfig;
  queryResults: CreditAgreement.Config.QueryResult[];
};
const EditDataCellObjectConfig = ({
  wipConfig,
  sheetIdxConfigPropertyKey,
  targetSheetConfig,
  queryResults,
}: EditDataCellObjectConfigProps) => {
  const [editMode, setEditMode] = useState<EditMode>('Data Cell');

  // As this is an internal component (parent component already manages permissions),
  // just utilizing clear safekeeping here on not rendering this component if
  // mutate permission is not allowed
  if (!wipConfig.isMutateAllowed(sheetIdxConfigPropertyKey)) {
    return null;
  }

  return (
    <Box id="edit-spreadsheet-container">
      <Flex alignItems="center" mb="7">
        <Text w="36" fontWeight="semibold">
          Data View Mode
        </Text>

        <Box w="80" mr="3">
          <Select<EditMode>
            value={editMode}
            onChange={(val) => {
              if (val) {
                setEditMode(val);
              }
            }}
            options={formatSelectOptions(EditModeIterable, 'title')}
          />
        </Box>
      </Flex>

      {editMode === 'Spreadsheet' && (
        <ReportSpreadsheetConfigView
          wipConfig={wipConfig}
          sheetIdxConfigPropertyKey={sheetIdxConfigPropertyKey}
          targetSheetConfig={targetSheetConfig}
          queryResults={queryResults}
        />
      )}
      {editMode === 'Data Cell' && (
        <ReportDataCellConfigView
          wipConfig={wipConfig}
          sheetIdxConfigPropertyKey={sheetIdxConfigPropertyKey}
          targetSheetConfig={targetSheetConfig}
          queryResults={queryResults}
        />
      )}
    </Box>
  );
};

type ReportDataCellObjectConfigContainerProps = {
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  sheetIdxConfigPropertyKey: string;
  targetSheetConfig: DataCellObjectConfig;
  queryResults: CreditAgreement.Config.QueryResult[];
};
export const ReportDataCellObjectConfigContainer = ({
  wipConfig,
  sheetIdxConfigPropertyKey,
  targetSheetConfig,
  queryResults,
}: ReportDataCellObjectConfigContainerProps) => {
  if (!wipConfig?.isMutateAllowed(sheetIdxConfigPropertyKey)) {
    return (
      <ViewDataCellObjectConfig
        sheetIdxConfigPropertyKey={sheetIdxConfigPropertyKey}
        targetSheetConfig={targetSheetConfig}
      />
    );
  }

  return (
    <EditDataCellObjectConfig
      wipConfig={wipConfig}
      sheetIdxConfigPropertyKey={sheetIdxConfigPropertyKey}
      targetSheetConfig={targetSheetConfig}
      queryResults={queryResults}
    />
  );
};
